import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiClock, FiTrendingUp, FiHeart, FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi'; // Added FiEye, FiEyeOff, FiCheckCircle
import { useFavorites } from '../hooks/useFavorites';
import { useAuth } from '../contexts/AuthContext';

interface MovieCardProps {
  id: string;
  poster: string;
  title: string;
  releaseDate: string;
  hypeScore: number;
  slug: string;
  watched?: boolean; // New prop
  onToggleWatched?: () => void; // New prop
  showWatchedToggle?: boolean; // New prop
}

const MovieCard: React.FC<MovieCardProps> = ({
  id,
  poster,
  title,
  releaseDate,
  hypeScore,
  slug,
  watched,
  onToggleWatched,
  showWatchedToggle,
}) => {
  const navigate = useNavigate();
  const { user: isSignedIn } = useAuth();
  const { toggleFavorite, isFavorite } = useFavorites();

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSignedIn) {
      toggleFavorite(id);
    } else {
      console.log("User not signed in. Cannot add to favorites.");
      navigate('/sign-in');
    }
  };

  const handleWatchedClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSignedIn && onToggleWatched) {
      onToggleWatched();
    }
  };

  const handleClick = () => {
    navigate(`/movie/${slug}`);
  };

  const getHypeColor = (score: number) => {
    if (score >= 8) return 'bg-accent-teal';
    if (score >= 6) return 'bg-primary';
    return 'bg-accent-orange';
  };

  const cardBaseClasses = "card group w-full md:w-48 cursor-pointer transition-all duration-300";
  const watchedClasses = watched && showWatchedToggle ? "opacity-70 hover:opacity-100" : "";


  return (
    <div
      className={`${cardBaseClasses} ${watchedClasses}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Poster Container */}
      <div className="relative overflow-hidden rounded-t-lg"> {/* Added rounded-t-lg for consistency */}
        <img
          src={poster}
          alt={title}
          className="w-full md:w-48 h-auto aspect-[2/3] object-cover transition-transform duration-300 group-hover:scale-105" // Slightly reduced scale
        />
        
        {/* Overlay on Hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-background-dark/90 via-background-dark/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-0 left-0 right-0 p-3"> {/* Adjusted padding */}
            <p className="text-white text-xs mb-1 flex items-center"> {/* Adjusted text size and margin */}
              <FiClock className="mr-1 w-3 h-3" /> {/* Adjusted icon size */}
              {new Date(releaseDate).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Hype Score Badge */}
        <div className="absolute top-2 right-2 flex items-center z-10">
          <div className={`${getHypeColor(hypeScore)} flex items-center space-x-1 px-2 py-1 rounded-full text-white text-xs font-semibold shadow-lg backdrop-blur-sm`}> {/* Adjusted text size */}
            <FiTrendingUp className="w-3 h-3" /> {/* Adjusted icon size */}
            <span>{Math.round(hypeScore)}</span>
          </div>
        </div>

        {/* Favorite Button */}
        {isSignedIn && (
          <button
            onClick={handleFavoriteClick}
            className="absolute top-2 left-2 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75 hover:text-red-400 transition-colors z-10"
            aria-label={isFavorite(id) ? 'Remove from favorites' : 'Add to favorites'}
          >
            <FiHeart className={`w-4 h-4 ${isFavorite(id) ? 'fill-red-500 text-red-500' : 'text-white'}`} /> {/* Adjusted icon size */}
          </button>
        )}

        {/* Watched Toggle Button - Placed at bottom right of poster */}
        {isSignedIn && showWatchedToggle && (
          <button
            onClick={handleWatchedClick}
            className={`absolute bottom-2 right-2 p-2 bg-black bg-opacity-60 rounded-full text-white hover:bg-opacity-80 transition-all z-10 ${watched ? 'bg-green-500 hover:bg-green-600' : 'hover:text-green-400'}`}
            aria-label={watched ? 'Mark as unwatched' : 'Mark as watched'}
          >
            {watched ? <FiCheckCircle className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
          </button>
        )}
         {/* Watched Indicator - subtle, if not using the toggle button for visual cue */}
         {watched && showWatchedToggle && (
            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-30 pointer-events-none group-hover:bg-opacity-10 transition-all duration-300">
                 <FiCheckCircle className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 text-white opacity-50" />
            </div>
        )}
      </div>

      {/* Content */}
      <div className={`p-3 bg-white rounded-b-lg ${watched && showWatchedToggle ? 'bg-gray-50' : ''}`}> {/* Added rounded-b-lg and conditional bg */}
        <h2
          className={`font-heading font-bold text-md mb-1 truncate text-secondary-dark group-hover:text-primary transition-colors duration-200 ${watched && showWatchedToggle ? 'text-gray-500' : ''}`} // Adjusted text size and conditional color
          title={title}
        >
          {title}
        </h2>
        <p className={`text-xs text-secondary-light font-medium ${watched && showWatchedToggle ? 'text-gray-400' : ''}`}> {/* Adjusted text size and conditional color */}
          {new Date(releaseDate).toLocaleDateString(undefined, {
            month: 'long',
            year: 'numeric'
          })}
        </p>
      </div>
    </div>
  );
};

export default MovieCard;